import * as React from "react";

import { useVizDispatch } from "@/shared/state/visualization";
import { setPlotPanelConfig } from "@/shared/state/visualization/actions";
import {
  PlotPanelConfig,
  TimeBoundsSource,
} from "@/shared/state/visualization/schema";
import { nanoSecToSecLossy } from "@/shared/time";

import { useWorkspaceTimeBounds } from "../../../WorkspaceCtx/hooks";
import { usePaddedTimeBounds } from "../hooks/usePaddedTimeBounds";
import { usePlotPanelContext } from "../panelContext";
import { Viewport } from "../Viewport";

import type { ContextSensitiveButtonProps } from "./types";

/**
 * Decides which behavior to give the context sensitive button.
 *
 * Decision tree:
 * 1. Is the panel is not hovered by the mouse?  Hide the button.
 * 2. Is the panel busy with a user interaction?  Hide the button.
 * 3. Is an axis of the panel locked and not interactable?  Show "Unlock View".
 * 4. Does the panel's topic data not match the panel's viewport?  Show "Fit to Data"
 * 5. Does the panel's viewport not match the panel's default time range?  Show "Reset View".
 * 6. Otherwise, hide the button.
 */
export function useButtonBehavior({
  state,
  chartExtents,
  localViewport,
  plotManager,
  isHostHovered,
  isHostIdle,
}: ContextSensitiveButtonProps): {
  hasBehavior: boolean;
  text: string | undefined;
  onClick: React.MouseEventHandler<HTMLButtonElement> | undefined;
} {
  const textRef = React.useRef<string | undefined>();
  let onClick: React.MouseEventHandler<HTMLButtonElement> | undefined =
    undefined;

  const vizDispatch = useVizDispatch();
  const hasViewChanged = React.useMemo(
    () => state.data.length > 0 && !localViewport.matchesDefaultViewport(),
    [state.data, localViewport],
  );
  const isViewLocked = React.useMemo(
    () => state.config?.yAxis?.bounds?.locked,
    [state.config],
  );
  const { isTopicDataVisible, topicDataViewport } = useTopicDataViewport({
    state,
    chartExtents,
    localViewport,
  });

  // Set up the button text and onClick behavior depending on context.
  const canHaveBehavior = isHostHovered && isHostIdle;
  if (!canHaveBehavior || state.data.length === 0 || !plotManager) {
    // A button that can't be shown should not change its text
    // so that it can fade out without any distracting changes.
    return { text: textRef.current, onClick: undefined, hasBehavior: false };
  }
  if (isViewLocked) {
    textRef.current = "Unlock View";
    onClick = function unlockView(event) {
      // Prevent triggering the other chart onclick handlers
      event.stopPropagation();
      const unlockedAxes: Partial<PlotPanelConfig> = {};
      if (state?.config?.yAxis?.bounds) {
        unlockedAxes.yAxis = {
          bounds: { ...state?.config?.yAxis?.bounds, locked: false },
        };
      }
      vizDispatch(setPlotPanelConfig(state.id, unlockedAxes));
    };
  } else if (
    !isTopicDataVisible &&
    topicDataViewport &&
    !topicDataViewport.matches(localViewport)
  ) {
    textRef.current = "Fit to Data";
    onClick = function fitToData(event) {
      // Prevent triggering the other chart onclick handlers
      event.stopPropagation();
      plotManager?.applyViewport(
        // The most recently-created viewport is what gets applied
        // to synced plot panels, so create a new viewport.
        new Viewport({
          ...topicDataViewport.getParams(),
          debounceId: undefined,
        }),
        "xy",
      );
    };
  } else if (hasViewChanged) {
    textRef.current = "Reset View";
    onClick = function resetView(event) {
      // Prevent triggering the other chart onclick handlers
      event.stopPropagation();
      plotManager?.resetView();
    };
  }
  return { text: textRef.current, onClick, hasBehavior: !!onClick };
}

function useTopicDataViewport({
  state,
  chartExtents,
  localViewport,
}: Pick<
  ContextSensitiveButtonProps,
  "state" | "chartExtents" | "localViewport"
>): {
  isTopicDataVisible: boolean;
  topicDataViewport?: Viewport;
} {
  const timeBounds = usePaddedTimeBounds(
    useWorkspaceTimeBounds({
      timeBoundsSource: TimeBoundsSource.TopicsInPanel,
      topicIds: state.data
        .map((d) => (d.visible ? d.data.topic.id : undefined))
        .filter((id) => id !== undefined),
    }),
  );
  const { isAligned } = usePlotPanelContext();
  return React.useMemo<{
    isTopicDataVisible: boolean;
    topicDataViewport?: Viewport;
  }>(
    function getActualDataViewport() {
      // The conversion below subtracts the chart's absolute start from
      // absolute topic bounds, which lands on the chart's x axis only while
      // every series shares one absolute basis. An aligned panel plots each
      // series as seconds elapsed from its own basis, so no absolute window
      // describes a range on that axis. Report the data as visible so the
      // button never offers "Fit to Data" on an aligned panel.
      if (isAligned) {
        return { isTopicDataVisible: true };
      }

      const domainMin = timeBounds.earliest;
      const domainMax = timeBounds.latest;

      // If there's no data, all the data is visible.
      if (
        domainMax === undefined ||
        domainMin === undefined ||
        chartExtents === null
      ) {
        return { isTopicDataVisible: true };
      }

      const topicDataBounds = {
        min: nanoSecToSecLossy(domainMin - chartExtents.data.fullLogTime.min),
        max: nanoSecToSecLossy(domainMax - chartExtents.data.fullLogTime.min),
      };
      const topicDataViewport = Viewport.fromDomainAndRange({
        dataDomain: chartExtents.data.fullDomain,
        dataRange: chartExtents.data.fullRange,
        viewportDomain: topicDataBounds,
        viewportRange: chartExtents.data.fullRange,
      });

      // If the viewports are an exact match, all the data is visible.
      if (topicDataViewport.matches(localViewport)) {
        return { isTopicDataVisible: true };
      }

      // Otherwise, the data is not visible.
      // Give actual bounds that can be used to show the data properly.
      return { isTopicDataVisible: false, topicDataViewport };
    },
    [chartExtents, timeBounds, localViewport, isAligned],
  );
}
